import React from "react";
import "./style.scss";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router";
import orderStore from "../../stores/order";
import {observer} from "mobx-react";
import {hideLoader, showLoader} from "../../helpers/ComponentHelper";
import {OrderStatus} from "../../@types/enums/OrderStatus";
import {PaymentMethod} from "../../@types/enums/PaymentType";
import {addToLog, getCurrency, getEatInSettings} from "../../helpers/ApiHelper";
import {OrderType} from "../../@types/enums/OrderType";

export interface FooterOrderProps {
    isFinalize?: boolean;
    checkTableNumberSettings?: boolean;
    canContinue?: boolean;
}

const FooterOrder = ({ isFinalize = false, checkTableNumberSettings = false, canContinue = true }: FooterOrderProps) => {
    const [t] = useTranslation();
    const navigate = useNavigate();

    const goToMenu = () => {
        if (orderStore.order.paidSum)
            return;

        navigate("/menu#ns");
    }

    const goToStart = async () => {
        await orderStore.cancelOrder();
        navigate("/");
    }

    const goToPaymentMethod = () => {
        if (!canContinue)
            return;

        if (checkTableNumberSettings && orderStore.order.type === OrderType.EAT_IN) {
            const eatInSettings = getEatInSettings();
            if (eatInSettings?.manualKitchenNumber && orderStore.tableNumber === -1) {
                navigate("/tableNumber");
            }
            else
                navigate("/paymentMethod");
            }
        else
            navigate("/paymentMethod");
    }

    const startPayment = async () => {
        if (!canContinue)
            return;

        if (orderStore.order.status === OrderStatus.START_PAYMENT)
            return;

        if (!orderStore.order.lines.length)
            return;

        showLoader();

        addToLog(`Start payment process`);

        const paymentMethod = orderStore.getPaymentMethod();

        // NOT INTERNAL CARD
        if (paymentMethod?.type !== PaymentMethod.GIFT_CARD) {
            const r = await orderStore.startPayment();

            if (r) {
                navigate("/payment");
            }
        }
        // INTERNAL CARD FROM CUSTOMER PAGE
        else if (paymentMethod?.type === PaymentMethod.GIFT_CARD && orderStore.terminalId === "internalCard") {
            orderStore.internalCard = orderStore.internalCardObj?.cardNumber || "";
            const r = await orderStore.startPayment();

            if (r) {
                navigate("/payment");
            }
        }
        else {
            navigate("/payment");
        }


        hideLoader();
    }

    return <>
        <div className="FooterOrderSpace" />
        <div className="FooterOrder">
            <div className="text">
                {orderStore.order.totalSubsidySum
                    ? <div className="row">
                        <div>{t("subsidy")}</div>
                        <div className="price">{orderStore.order.totalSubsidySum.toFixed(2)} {getCurrency()}</div>
                    </div>
                    : null
                }
                <div className="row">
                    <div>{t("subtotal")}</div>
                    <div className="price">{(orderStore.order.total - (orderStore.order.totalSubsidySum || 0) - orderStore.order.vatAmount).toFixed(2)} {getCurrency()}</div>
                </div>
                <div className="row">
                    <div>{t("vat")}</div>
                    <div className="price">{orderStore.order.vatAmount.toFixed(2)} {getCurrency()}</div>
                </div>
                <div className="row total">
                    <div>{t("total")}</div>
                    <div className="price">{(orderStore.order.total  - (orderStore.order.totalSubsidySum || 0)).toFixed(2)} {getCurrency()}</div>
                </div>
                {orderStore.order.paidSum
                    ? <div className="row paid">
                        <div>{t("paid")}</div>
                        <div className="price">{orderStore.order.paidSum.toFixed(2)} {getCurrency()}</div>
                    </div>
                    : null
                }
                {orderStore.order.discount
                    ? <div className="row discount">
                        <div>{t("discount")}</div>
                        <div className="price">{orderStore.order.discount.toFixed(2)} {getCurrency()}</div>
                    </div>
                    : null
                }
            </div>
            <div className="buttons">
                <div className={`button ${orderStore.order.paidSum ? "disable" : ""}`} onClick={goToMenu}>{t("orderMore")}</div>
                <div className={`button dark ${orderStore.order.lines.length === 0 ? "disable" : ""} ${!canContinue ? "disable" : ""}`} onClick={isFinalize ? startPayment : goToPaymentMethod}>
                    <div className="content">
                        {orderStore.order.lines.length > 0
                            ? <div className="count">
                                <div className="text">
                                    {orderStore.order.lines.length}
                                </div>
                            </div>
                            : <div />
                        }
                        {t(isFinalize ? "finalize" : "continueToPayment")}
                        <div className="price">
                            {orderStore.order.total > 0
                                ? `${(orderStore.order.total - (orderStore.order.totalSubsidySum || 0) - orderStore.order.paidSum).toFixed(2)} ${getCurrency()}`
                                : null
                            }
                        </div>
                    </div>
                </div>
            </div>
            <div className="startOverContainer">
                <div className="button startOver noBorder" onClick={goToStart}>{t("startOver")}</div>
            </div>
        </div>
    </>
}

export default observer(FooterOrder);
